import { PayTable } from "@lib/titan2-slingo/dist/game/view/abstract/display/PayTable";
import { TitanSpine } from "@lib/titan2/dist/game/control/TitanSpine";
import { OrientationType } from "@lib/titan2/dist/game/renderer/constant/RendererConstants";
import { gsap } from "gsap";
import { PixiPlugin } from "gsap/all";
import { SlingoEvent } from "@lib/titan2-slingo/dist/game/controller/event/SlingoEvent";
import { addToSpineSlot, addToTitanSpineSlot, hasTitanSpineSlot, resetTitanSpine, updateResponsiveSpineSlotsChildren } from "../../../utils/SpineUtils";
import { TitanText } from "@lib/titan2/dist/game/control/TitanText";
import { GameLang } from "../lang/GameLang";
import { EventObject } from "@lib/titan2/dist/util/event/EventObject";
import { SpineUtil } from "@lib/titan2/dist/util/view/SpineUtil";
import { TitanSprite } from "@lib/titan2/dist/game/control/TitanSprite";
gsap.registerPlugin(PixiPlugin);
/**
 * GamePayTable.ts
 */
export class GamePayTable extends PayTable {
    create() {
        this.createModel();
        this.animation = TitanSpine.createSpine(this, this.landscapeProperties.animationProperties, this.portraitProperties?.animationProperties);
        this.createCells();
        this.createBackToGameButton();
        this.setPopup(false);
    }
    setPopup(isPopup) {
        const skin = isPopup ? "freestanding" : "ingame";
        if (this.animation.getSpine().skeleton.data.findSkin(skin)) {
            this.animation.setSkin(skin);
        }
    }
    postCreate() {
        super.postCreate();
    }
    createCells() {
        this.cellTexts = [];
        const bonusGameLabels = {
            5: this.langManager.getLang(GameLang.SLINGO_5),
            6: this.langManager.getLang(GameLang.SLINGO_6),
            7: this.langManager.getLang(GameLang.SLINGO_7),
            8: this.langManager.getLang(GameLang.SLINGO_8),
            9: this.langManager.getLang(GameLang.SLINGO_9),
            10: this.langManager.getLang(GameLang.SLINGO_10),
        };
        for (let i = 4; i < 10; i++) {
            let label = undefined;
            let currentHighlightLabel = undefined;
            let prizeExceededLabel = undefined;
            if (this.landscapeProperties.cellLabel) {
                const labelString = bonusGameLabels[i + 1];
                label = TitanText.createField(null, this.landscapeProperties.lineProperties[i].label, this.portraitProperties?.lineProperties[i].label);
                label.setText(labelString);
                currentHighlightLabel = TitanText.createField(null, this.landscapeProperties.lineProperties[i].currentHighlightLabel, this.portraitProperties?.lineProperties[i].currentHighlightLabel);
                currentHighlightLabel.setText(labelString);
                prizeExceededLabel = TitanText.createField(null, this.landscapeProperties.lineProperties[i].prizeExceededLabel, this.portraitProperties?.lineProperties[i].prizeExceededLabel);
                prizeExceededLabel.setText(labelString);
            }
            const value = TitanText.createField(null, this.landscapeProperties.cellValue, this.portraitProperties?.cellValue);
            const currentHighlightValue = TitanText.createField(null, this.landscapeProperties.cellCurrentHighlightValue, this.portraitProperties?.cellCurrentHighlightValue);
            const prizeExceededValue = TitanText.createField(null, this.landscapeProperties.cellPrizeExceededValue, this.portraitProperties?.cellPrizeExceededValue);
            value.setText(``, true);
            currentHighlightValue.setText(``, true);
            prizeExceededValue.setText(``, true);
            this.cellTexts.push({ label, prizeExceededLabel, currentHighlightLabel, value, currentHighlightValue, prizeExceededValue });
        }
        if (hasTitanSpineSlot(this.animation, `dynamic-label-11`)) {
            if (this.shouldLocalise()) {
                const fullHouseText = TitanText.createField(null, this.landscapeProperties.fullHouseDynamicProperties, this.portraitProperties?.fullHouseDynamicProperties);
                addToTitanSpineSlot(this.animation, fullHouseText, "dynamic-label-11");
            }
            else {
                const fullHouseLabelGraphic = TitanSprite.createSprite(null, "fullHouseLabelGraphic", this.landscapeProperties.fullHouseGraphicProperties, this.portraitProperties?.fullHouseGraphicProperties);
                addToTitanSpineSlot(this.animation, fullHouseLabelGraphic, "dynamic-label-11");
            }
        }
        this.updateCellTextOrientation();
    }
    shouldLocalise() {
        return !this.langManager.getLocale().getId().includes("en");
    }
    updateCellTextOrientation() {
        this.cellTexts.forEach((cellText, i) => {
            i += 5;
            if (i > 10) {
                return;
            }
            const slotName = `dynamic-label-${i}`;
            if (cellText.label) {
                addToSpineSlot(this.animation.getSpine(this.renderer.getOrientation()), cellText.label, slotName);
            }
            if (cellText.currentHighlightLabel) {
                addToSpineSlot(this.animation.getSpine(this.renderer.getOrientation()), cellText.currentHighlightLabel, slotName);
            }
            if (cellText.prizeExceededLabel) {
                addToSpineSlot(this.animation.getSpine(this.renderer.getOrientation()), cellText.prizeExceededLabel, slotName);
            }
            if (cellText.value) {
                addToSpineSlot(this.animation.getSpine(this.renderer.getOrientation()), cellText.value, slotName);
            }
            if (cellText.currentHighlightValue) {
                addToSpineSlot(this.animation.getSpine(this.renderer.getOrientation()), cellText.currentHighlightValue, slotName);
            }
            if (cellText.prizeExceededValue) {
                addToSpineSlot(this.animation.getSpine(this.renderer.getOrientation()), cellText.prizeExceededValue, slotName);
            }
        });
    }
    numberOfPatternsToCell(numberOfPatterns) {
        return numberOfPatterns === 12 ? 11 : numberOfPatterns;
    }
    handleAvailableWin() {
        const availableWin = this.model.getAvailableWin();
        const numberOfPatterns = availableWin.getNumberOfPatterns();
        SpineUtil.playLoop(this.animation, `2-potential-${this.numberOfPatternsToCell(numberOfPatterns)}`, 2);
    }
    handleSpinRequest() {
        if (this.model.isPurchaseSpin()) {
            this.handleAvailableWinClear();
        }
    }
    handleCollectRequest() {
        this.handleAvailableWinClear();
    }
    handleAvailableWinClear() {
        this.getSpineOrientations().forEach((spine) => {
            spine.state.clearTrack(2);
            spine.skeleton.setToSetupPose();
        });
    }
    clearHighlight() {
        this.getSpineOrientations().forEach((spine) => {
            spine.state.clearTracks();
            spine.skeleton.setToSetupPose();
        });
    }
    getSpineOrientations() {
        const orientations = [];
        const landscape = this.animation.getSpine(OrientationType.LANDSCAPE);
        if (landscape) {
            orientations.push(landscape);
        }
        const portrait = this.animation.getSpine(OrientationType.PORTRAIT);
        if (portrait) {
            orientations.push(portrait);
        }
        return orientations;
    }
    updatePrizeValues() {
        this.dispatchGame(SlingoEvent.PAY_TABLE_UPDATE);
    }
    updatePrizeValueForCell(cellText, payoutMultiplier, stake) {
        cellText.value.setText(this.formatCurrency(payoutMultiplier * stake), true);
        cellText.prizeExceededValue.setText(this.formatCurrency(payoutMultiplier * stake), true);
        cellText.currentHighlightValue.setText(this.formatCurrency(payoutMultiplier * stake), true);
    }
    handleSlingoPayTable() {
        let delay = 0;
        let slingosAwarded = this.model.getSlingosAwarded();
        let matchedPatterns = this.model.getCurrentMatchedPatterns();
        for (let index = slingosAwarded - 1; index >= 0; index--) {
            const anticipationTrack = (2 + slingosAwarded) - index;
            this.callAfter(delay, this.clearAnticipationTrack, anticipationTrack);
            this.callAfter(delay, this.handleMatchedPatterns, matchedPatterns - index);
            delay += matchedPatterns >= 5 ? this.uiProperties.matchedPatternInterval : this.uiProperties.losingMatchedPatternInterval;
        }
        this.callAfter(delay, this.handleMatchedPatternsComplete);
    }
    clearAnticipationTrack(track) {
        this.getSpineOrientations().forEach((spine) => {
            spine.state.clearTrack(track);
            spine.skeleton.setToSetupPose();
        });
    }
    updateCells(numberOfPatterns) {
        if (numberOfPatterns === 11) {
            return;
        }
        this.cellTexts.forEach(({ currentHighlightLabel, currentHighlightValue, prizeExceededLabel, prizeExceededValue, label, value }) => {
            prizeExceededLabel?.hide();
            prizeExceededValue?.hide();
            currentHighlightLabel?.hide();
            currentHighlightValue?.hide();
            label?.hide();
            value?.hide();
        });
        const currentlyHighlightPrizeRow = this.numberOfPatternsToCell(numberOfPatterns);
        const currentlyHighlightPrizeIndex = currentlyHighlightPrizeRow - 1;
        this.cellTexts.forEach(({ label, value, prizeExceededLabel, prizeExceededValue, currentHighlightValue, currentHighlightLabel }, i) => {
            if (i < currentlyHighlightPrizeIndex) {
                prizeExceededLabel?.show();
                prizeExceededValue?.show();
            }
            else if (i === currentlyHighlightPrizeIndex) {
                currentHighlightLabel?.show();
                currentHighlightValue?.show();
            }
            else {
                label?.show();
                value?.show();
            }
        });
        const idle = this.animation.getTrackEntry(4);
        const frame = idle ? idle.trackTime * 30 : 0;
        this.animation.removeCompleteHandlers();
        resetTitanSpine(this.animation);
        SpineUtil.playLoop(this.animation, `4-idle-all`, 4);
        SpineUtil.gotoAndPlay(this.animation, frame, `4-idle-all`, 4, true);
        SpineUtil.play(this.animation, `0-level-${Math.max(0, currentlyHighlightPrizeRow)}`, null, null, 0);
        if (numberOfPatterns > 0) {
            SpineUtil.play(this.animation, `3-highlight-intro-${currentlyHighlightPrizeRow}`, null, null, 3);
            this.animation.onComplete(() => {
                this.animation.removeCompleteHandlers();
                this.animation.clearTrack(3);
                SpineUtil.playLoop(this.animation, `1-highlight-${currentlyHighlightPrizeRow}`, 1);
            }, null, 3);
        }
        else {
            this.animation.clearTrack(3);
            SpineUtil.playLoop(this.animation, `1-highlight-${currentlyHighlightPrizeRow}`, 1);
        }
        if (numberOfPatterns === 12) {
            SpineUtil.play(this.animation, `0-level-11`, null, null, 0);
            SpineUtil.play(this.animation, `1-highlight-0`, null, null, 1);
            SpineUtil.play(this.animation, `3-highlight-intro-${currentlyHighlightPrizeRow}`, null, null, 3);
            this.animation.onComplete(() => {
                this.animation.removeCompleteHandlers();
                this.animation.clearTrack(3);
                SpineUtil.playLoop(this.animation, `1-highlight-${currentlyHighlightPrizeRow}`, 1);
            }, null, 3);
        }
    }
    handleRotate(event) {
        const data = event?.getCurrentTarget() || {};
        this.visible = this.renderer.isLandscape(data.forceOrientation);
        if (this.backToGameButton) {
            this.backToGameButton.visible = this.renderer.isPortrait(data.forceOrientation);
        }
        this.updateCellTextOrientation();
        updateResponsiveSpineSlotsChildren(this.animation);
    }
}
export class GamePaytableEvents {
}
GamePaytableEvents.TRANSITION_TO_FEATURE_FADE_OUT = new EventObject("GamePaytableEvents.TRANSITION_TO_FEATURE_FADE_OUT");
GamePaytableEvents.TRANSITION_FROM_FEATURE_FADE_IN = new EventObject("GamePaytableEvents.TRANSITION_FROM_FEATURE_FADE_IN");
//# sourceMappingURL=GamePayTable.js.map